// "Which inboxes feed this project" (docs/Y2026-Q3/design-mailbox-multi-inbox.md).
// The VIEWER adds only their own inbox: the OAuth grant is the consent boundary
// (design section 4). Adding an inbox NEEDS that grant, so this card offers to
// create one rather than assuming it exists.
import { useContext, useEffect, useState } from "react";
import { useQueryClient } from "react-query";

import {
  useConnectProjectMailbox,
  useDisconnectProjectMailbox,
  useProjectMailboxes,
  useSweepProjectMailboxesNow,
  type ProjectMailbox,
  type SweepNowRow,
} from "../api/hooks/useProjectMailboxes";
import { useOperationWatcher } from "../api/hooks/useAsyncOperation";
import { useGoogleAuthStatus } from "../api/hooks/useDriveSync";
import { queryKeys } from "../api/queryKeys";
import { UserContext } from "../auth";
import { useConnectGoogle } from "../utils/useConnectGoogle";
import styles from "./AdditionalInboxesCard.module.css";

// Connecting Google is a full-page redirect, so the intent to add an inbox has
// to survive it. Holds the project id the operator clicked on; the card resumes
// the add on the way back and clears it. Session-scoped, like the return-to
// path it travels beside.
const ADD_INBOX_AFTER_CONNECT_KEY = "inrange.add_inbox_after_connect";

interface Props {
  projectId: string;
}

const coldStartNote = (state: string | null): string => {
  if (state === "pending" || state === "expanding") {
    return "searching this inbox's history";
  }
  if (state === "completed") return "history searched";
  return "";
};

const InboxRow = ({
  mailbox,
  onDisconnect,
  busy,
}: {
  mailbox: ProjectMailbox;
  onDisconnect: (accountEmail: string) => void;
  busy: boolean;
}) => {
  const notes = [
    coldStartNote(mailbox.cold_start_state),
    mailbox.status === "connected" ? "" : mailbox.status,
  ].filter(Boolean);
  return (
    <li className={styles.row}>
      <div>
        <span className={styles.account}>{mailbox.account_email}</span>
        {notes.length > 0 && (
          <span className={styles.notes}> - {notes.join(", ")}</span>
        )}
      </div>
      <button
        type="button"
        className={styles.unlink}
        disabled={busy}
        onClick={() => onDisconnect(mailbox.account_email)}
      >
        Remove
      </button>
    </li>
  );
};

// What the sweep is doing right now for one inbox, polled from its operation key
// rather than going quiet after "it runs in the background".
const SweepProgress = ({
  projectId,
  row,
}: {
  projectId: string;
  row: SweepNowRow;
}) => {
  const qc = useQueryClient();
  // The sweep moves last_swept_at and can add threads.
  const op = useOperationWatcher(row.operation_key, () => {
    void qc.invalidateQueries(queryKeys.projectMailboxes(projectId));
  });
  const status = op.data?.status;

  const progress = op.data?.progress;
  const label = (() => {
    if (status === "failed") return `failed: ${op.data?.error ?? "unknown error"}`;
    if (status === "cancelled") return "cancelled";
    if (status === "completed") return "swept";
    if (!op.data) return "queued";
    if (status === "pending") return "queued - waiting for the account's turn";
    // total is null while the listing is still gathering candidates.
    const stage = progress?.label ?? "sweeping";
    if (!progress) return stage;
    return progress.total === null
      ? `${stage} (${progress.done})`
      : `${stage} (${progress.done} of ${progress.total})`;
  })();

  return (
    <p className={status === "failed" ? styles.error : styles.hint}>
      {row.account_email}: {label}
    </p>
  );
};

// What "Sync now" did per inbox, naming why a skipped one was not swept.
const SweepNowResult = ({
  projectId,
  rows,
}: {
  projectId: string;
  rows: SweepNowRow[];
}) => (
  <div className={styles.sweepResult}>
    {rows
      .filter((row) => row.staged)
      .map((row) => (
        // Keyed on the OPERATION: keyed on the inbox, a second "Sync now" would
        // reuse the finished row's state and never poll.
        <SweepProgress
          key={row.operation_key ?? row.account_email}
          projectId={projectId}
          row={row}
        />
      ))}
    {rows.filter((row) => !row.staged).length > 0 &&
      rows.filter((row) => row.staged).length === 0 && (
        <p className={styles.description}>No sweep was queued.</p>
      )}
    {rows
      .filter((row) => !row.staged)
      .map((row) => (
        <p key={row.account_email} className={styles.hint}>
          {row.account_email}: {row.message}
        </p>
      ))}
  </div>
);

export const AdditionalInboxesCard = ({ projectId }: Props) => {
  const list = useProjectMailboxes(projectId);
  const connect = useConnectProjectMailbox(projectId);
  const disconnect = useDisconnectProjectMailbox(projectId);
  const sweepNow = useSweepProjectMailboxesNow(projectId);
  const [added, setAdded] = useState(false);

  const { user } = useContext(UserContext);
  const authStatus = useGoogleAuthStatus();
  const { connect: connectGoogle, connecting } = useConnectGoogle(
    user?.email ?? undefined,
  );
  const authorization = authStatus.data?.authorization ?? null;
  const googleConnected = authorization?.status === "active";

  // Back from the consent screen: finish what the click started. Gated on the
  // grant actually being active, so a declined or failed authorization leaves
  // the operator on the button rather than firing a request that would 400.
  const connectMutate = connect.mutate;
  useEffect(() => {
    if (!googleConnected || authStatus.isLoading) return;
    if (sessionStorage.getItem(ADD_INBOX_AFTER_CONNECT_KEY) !== projectId) return;
    sessionStorage.removeItem(ADD_INBOX_AFTER_CONNECT_KEY);
    connectMutate(undefined, { onSuccess: () => setAdded(true) });
  }, [googleConnected, authStatus.isLoading, projectId, connectMutate]);

  const mailboxes = list.data?.mailboxes ?? [];
  const busy =
    connect.isLoading || disconnect.isLoading || sweepNow.isLoading || connecting;

  return (
    <section className={styles.card}>
      <header className={styles.header}>
        <h3 className={styles.title}>Connected inboxes</h3>
        <span className={styles.count}>
          {mailboxes.length} connected
        </span>
      </header>

      {list.isLoading && <p className={styles.description}>Loading inboxes...</p>}
      {list.isError && (
        <p className={styles.error}>
          Could not load this project&apos;s inboxes.{" "}
          {list.error instanceof Error ? list.error.message : ""}
        </p>
      )}
      {!list.isLoading && mailboxes.length === 0 && (
        <p className={styles.description}>
          No inbox feeds this project yet.
        </p>
      )}
      {mailboxes.length > 0 && (
        <ul className={styles.list}>
          {mailboxes.map((mailbox) => (
            <InboxRow
              key={mailbox.mailbox_id}
              mailbox={mailbox}
              busy={busy}
              onDisconnect={(accountEmail) => {
                setAdded(false);
                disconnect.mutate({ accountEmail });
              }}
            />
          ))}
        </ul>
      )}

      <p className={styles.description}>
        The sweep reads every inbox listed here and files what belongs to this
        project, so mail that only ever reached a colleague&apos;s inbox lands here
        too. A conversation held in two inboxes is ingested once.
      </p>

      <button
        type="button"
        className={styles.connect}
        disabled={busy || authStatus.isLoading}
        onClick={() => {
          setAdded(false);
          if (!googleConnected) {
            // Remember the intent across the redirect, then come back and add.
            sessionStorage.setItem(ADD_INBOX_AFTER_CONNECT_KEY, projectId);
            connectGoogle();
            return;
          }
          connect.mutate(undefined, { onSuccess: () => setAdded(true) });
        }}
      >
        {connect.isLoading
          ? "Adding..."
          : connecting
            ? "Opening Google..."
            : googleConnected
              ? "Add my inbox"
              : "Connect Google and add my inbox"}
      </button>
      <p className={styles.hint}>
        {googleConnected
          ? "Adds the Google account you signed in to Gmail with."
          : "You have not connected a Google account yet, so this asks Google for read-only access to your Gmail first, then adds that inbox."}{" "}
        A colleague adds theirs by opening this project and doing the same - only
        the person whose Google account it is can connect or remove it.
      </p>

      {connect.error && (
        <p className={styles.error}>
          Could not add your inbox. {connect.error.message}
        </p>
      )}
      {disconnect.error && (
        <p className={styles.error}>
          Could not remove that inbox. {disconnect.error.message}
        </p>
      )}
      {/* Skips the daily staggered slot for the moments somebody is watching;
          a busy account or Google rate-limit still holds. Last, as it acts on
          the whole list above. */}
      <div className={styles.sweepRow}>
        <button
          type="button"
          className={styles.sweepNow}
          disabled={busy || mailboxes.length === 0}
          onClick={() => sweepNow.mutate()}
        >
          {sweepNow.isLoading ? "Queueing..." : "Sync now"}
        </button>
        {sweepNow.error && (
          <p className={styles.error}>
            Could not queue a sync. {sweepNow.error.message}
          </p>
        )}
        {sweepNow.data && (
          <SweepNowResult projectId={projectId} rows={sweepNow.data.mailboxes} />
        )}
      </div>
      {added && !connect.error && (
        <p className={styles.added}>
          Your inbox now feeds this project. Its history is searched over the next
          few sweeps, so anything already there arrives gradually rather than all
          at once.
        </p>
      )}
    </section>
  );
};
